import React, { useState } from 'react';
import api from '../api';

const getBmiColor = (score) => {
  if (score === null || score === undefined || score === '') return '#74746a';
  const numericScore = Number(score);
  if (!Number.isFinite(numericScore)) return '#74746a';
  if (numericScore < 18.5) return '#3B82F6';
  if (numericScore < 25) return '#16A34A';
  if (numericScore < 30) return '#F59E0B';
  return '#EF4444';
};

const DietPlanSystem = ({ data }) => {
  const [activeDay, setActiveDay]       = useState(1);
  const [shoppingList, setShoppingList] = useState(null);
  const [loadingList, setLoadingList]   = useState(false);
  const [openCategory, setOpenCategory] = useState(null);

  if (!data) return null;

  const fetchShoppingList = async () => {
    setLoadingList(true);
    try {
      const response = await api.post('/dietplan/shoppinglist', { id: data.id });
      setShoppingList(response.data);
    } catch (err) {
      console.error('Error fetching shopping list:', err.response?.data || err.message);
      alert('Failed to generate shopping list. Please try again.');
    } finally {
      setLoadingList(false);
    }
  };

  const currentDayPlan = data.meal_plan.find(d => d.day === activeDay);
  const totalDays      = data.meal_plan.length;
  const bmiData = data.profile.physicalMetrics.bmi_data;
  const bmiColor = getBmiColor(bmiData.score);

  return (
    <div className="diet-plan-wrapper">
      {/* ── Page Title ── */}
      <div className="diet-plan-heading">
        <h2>
          Your {totalDays}-Day Diet Plan 🥗
        </h2>
        <p>
          Cuisine: <strong>{data.profile.cuisine_preference}</strong>
          &nbsp;·&nbsp;
          Lifestyle: <strong>{data.profile.lifestyle}</strong>
        </p>
      </div>

      <div className="diet-plan-grid">

        {/* ══ Left Sidebar ══ */}
        <div>
          {/* Profile Card */}
          <div className="card-custom diet-plan-profile-card">
            <div className="diet-plan-card-header">
              <h6 style={{ color: '#fff', margin: 0, fontWeight: 700, fontSize: '0.9rem' }}>Profile Summary</h6>
            </div>
            <div className="diet-plan-card-body">
              {/* BMI */}
              <div className="diet-plan-bmi" style={{
                '--bmi-color': bmiColor,
                borderColor: bmiColor,
                backgroundColor: `${bmiColor}14`,
              }}>
                <div className="diet-plan-bmi-label">
                  Current BMI
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="diet-plan-bmi-score" style={{ color: bmiColor }}>
                    {bmiData.score}
                  </span>
                  <span className="diet-plan-bmi-category" style={{ color: bmiColor, borderColor: bmiColor }}>
                    {bmiData.category}
                  </span>
                </div>
              </div>

              {/* Stats */}
              {[
                ['Age / Gender',    `${data.profile.age} / ${data.profile.gender}`],
                ['Height',          `${data.profile.physicalMetrics.height.value} ${data.profile.physicalMetrics.height.unit}`],
                ['Current Weight',  `${data.profile.physicalMetrics.current_weight.value} ${data.profile.physicalMetrics.current_weight.unit || 'kg'}`],
                ['Goal Weight',     `${data.profile.physicalMetrics.goal_weight.value} ${data.profile.physicalMetrics.goal_weight.unit || 'kg'}`],
              ].map(([label, value], i) => (
                <div key={i} className="profile-stat-row">
                  <span className="profile-stat-label">{label}</span>
                  <span className="profile-stat-value">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Health Tags */}
          {(data.profile.medical_conditions?.cholesterol ||
            data.profile.medical_conditions?.diabetes ||
            data.profile.medical_conditions?.hypertension) && (
            <div className="card-custom" style={{ padding: '16px 20px', marginBottom: '16px' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: '10px' }}>
                Health Flags
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {data.profile.medical_conditions?.cholesterol  && <span className="badge-custom badge-gold">High Cholesterol</span>}
                {data.profile.medical_conditions?.diabetes     && <span className="badge-custom badge-orange">Diabetes</span>}
                {data.profile.medical_conditions?.hypertension && <span className="badge-custom badge-red">Hypertension</span>}
              </div>
            </div>
          )}

          {/* Shopping List */}
          <div className="card-custom" style={{ marginBottom: '16px' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--color-text)' }}>🛒 Shopping List</span>
              <button
                className="btn-green-custom"
                onClick={fetchShoppingList}
                disabled={loadingList}
                style={{ padding: '8px 16px', fontSize: '0.8rem', boxShadow: 'none',
                  ...(loadingList ? { opacity: 0.65, cursor: 'not-allowed', transform: 'none' } : {}) }}
              >
                {loadingList ? 'Loading…' : shoppingList ? 'Refresh' : 'Get List'}
              </button>
            </div>
            <div style={{ padding: '16px 20px' }}>
              {shoppingList ? (
                <div>
                  {Object.keys(shoppingList).map((category, index) => (
                    <div key={category} className="shop-category">
                      <button
                        className="shop-category-header"
                        onClick={() => setOpenCategory(openCategory === index ? null : index)}
                      >
                        <span>{category.replace(/_/g, ' ')}</span>
                        <span>{openCategory === index ? '▲' : '▼'}</span>
                      </button>
                      {openCategory === index && (
                        <div>
                          {shoppingList[category].map((item, i) => (
                            <div key={i} className="shop-item">
                              <span className="shop-item-name">{item.item}</span>
                              <span className="shop-item-qty">{item.quantity}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem', margin: 0 }}>
                  Generate a grocery list for your full meal plan.
                </p>
              )}
            </div>
          </div>

          {/* Tips */}
          {data.lifestyle_tips && data.lifestyle_tips.length > 0 && (
            <div className="card-custom" style={{ padding: '16px 20px' }}>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--color-text)', marginBottom: '12px' }}>
                💡 Lifestyle Tips
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {data.lifestyle_tips.map((tip, i) => (
                  <li key={i} style={{
                    fontSize: '0.82rem',
                    color: 'var(--color-text-muted)',
                    padding: '6px 0',
                    borderBottom: i < data.lifestyle_tips.length - 1 ? '1px solid var(--color-border)' : 'none',
                    lineHeight: 1.55,
                    display: 'flex',
                    gap: '8px',
                  }}>
                    <span style={{ color: 'var(--color-primary)', flexShrink: 0 }}>›</span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* ══ Main — Meal Plan ══ */}
        <div>
          <div className="card-custom diet-plan-meals-card">
            {/* Day Selector */}
            <div className="diet-plan-day-header">
              <div className="diet-plan-day-label">
                Select Day
              </div>
              <div className="day-selector-bar">
                {data.meal_plan.map((day) => (
                  <button
                    key={day.day}
                    className={`day-btn ${activeDay === day.day ? 'active' : ''}`}
                    onClick={() => setActiveDay(day.day)}
                  >
                    {day.day}
                  </button>
                ))}
              </div>
            </div>

            {/* Meals for Selected Day */}
            <div className="diet-plan-day-content">
              <h4 className="diet-plan-day-title">
                Day {activeDay} — Meal Schedule
              </h4>
              {currentDayPlan?.meals.map((meal, idx) => (
                <div key={idx} className="meal-timeline-item" style={{ animationDelay: `${idx * 0.08}s` }}>
                  <div className="meal-time-col">
                    <span className="meal-time-label">{meal.time}</span>
                    {idx < currentDayPlan.meals.length - 1 && (
                      <div className="meal-time-line" />
                    )}
                  </div>
                  <div className="meal-card">
                    <div className="meal-name">{meal.name}</div>
                    <ul className="meal-items-list">
                      {meal.items.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default DietPlanSystem;
